/**
 * Recovery for a tab that outlived the deployment it was loaded from.
 *
 * Every screen in this SPA is a lazy chunk (`registry`'s
 * `load: () => import(...)`, resolved by `App.tsx`'s `lazy()`), and the chunk
 * filenames are content-hashed. A deploy replaces them and deletes the old
 * ones, so a tab that was already open asks for a hash the origin no longer
 * has, gets a 404, and the dynamic import rejects.
 *
 * Nothing caught that. There is no error boundary above the router, so the
 * rejection escaped, React rendered nothing, and the customer was left with a
 * blank white page carrying no message — indistinguishable from the portal
 * being down, which is exactly what it was reported as. Three deploys in one
 * morning put a real mid-journey customer through it.
 *
 * The fix is the one the platform intends: reload once, so the browser fetches
 * the current `index.html` (served `max-age=30, must-revalidate`) and with it
 * the current chunk names. The reload is **guarded**, because a chunk that is
 * missing for any other reason would otherwise loop the page forever: one
 * recovery per cool-down window, recorded in `sessionStorage` so it is scoped
 * to this tab and cannot outlive it.
 *
 * If the guard refuses, we let the failure through untouched rather than
 * swallow it — a page stuck in a reload loop is worse than a page that fails
 * once, visibly.
 */

/** Marker for "this tab has already reloaded itself to recover". */
export const RELOAD_MARKER = 'scope.staleChunkReloadedAt'

/**
 * Long enough that a reload has certainly completed and the app has settled,
 * short enough that a customer who hits a second, genuinely-new deploy later
 * in the same session still recovers automatically.
 */
export const RELOAD_COOLDOWN_MS = 30_000

/** The message browsers use when a dynamic import cannot be fetched. */
export function isStaleChunkError(reason: unknown): boolean {
  const message =
    reason instanceof Error ? reason.message : typeof reason === 'string' ? reason : ''

  return (
    /failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message)
  )
}

export interface ReloadGuard {
  readonly getItem: (key: string) => string | null
  readonly setItem: (key: string, value: string) => void
}

/**
 * The recorded recovery timestamp, or `null` for "never" — which is NOT the
 * same as the timestamp 0. Reading an absent marker as 0 made `now - last`
 * small for any small clock and refused the very first recovery, which is the
 * whole point of the guard.
 *
 * A storage that throws (private mode, blocked cookies) reads as "never"
 * rather than crashing: a recoverable stale chunk must not become an
 * unrecoverable one.
 */
function readMarker(storage: ReloadGuard): number | null {
  try {
    const raw = storage.getItem(RELOAD_MARKER)
    if (raw === null) return null
    const parsed = Number(raw)

    return Number.isFinite(parsed) ? parsed : null
  } catch {
    return null
  }
}

/**
 * `true` when this tab may reload itself to pick up the current bundle.
 * Records the attempt, so the answer is `false` for the whole cool-down.
 */
export function mayReloadForStaleChunk(storage: ReloadGuard, nowMs: number): boolean {
  const last = readMarker(storage)
  if (last !== null && nowMs - last < RELOAD_COOLDOWN_MS) return false

  try {
    storage.setItem(RELOAD_MARKER, String(nowMs))
  } catch {
    // Cannot record the attempt ⇒ cannot guard against a loop ⇒ do not start one.
    return false
  }

  return true
}

export interface StaleChunkHost {
  readonly addEventListener: (type: string, listener: (event: Event) => void) => void
  readonly sessionStorage: ReloadGuard
  readonly location: { readonly reload: () => void }
}

/**
 * Wires both routes a failed chunk takes to the top: Vite's own
 * `vite:preloadError` (fired by its preload helper before the import rejects)
 * and the unhandled rejection of the `import()` itself, which is what escapes
 * when the module is fetched without a preload.
 */
export function installStaleChunkRecovery(
  host: StaleChunkHost,
  now: () => number = Date.now,
): void {
  const recover = (reason: unknown): void => {
    if (!isStaleChunkError(reason)) return
    if (!mayReloadForStaleChunk(host.sessionStorage, now())) return

    host.location.reload()
  }

  host.addEventListener('vite:preloadError', (event) => {
    recover((event as Event & { payload?: unknown }).payload ?? event)
  })
  host.addEventListener('unhandledrejection', (event) => {
    recover((event as Event & { reason?: unknown }).reason)
  })
}
